import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Container,
  Form,
  Button,
  Table,
  Alert,
  Modal,
} from "react-bootstrap";
import {
  ShoppingCart,
  CreditCard,
  Percent,
  DollarSign,
  Package,
  Info,
  CheckCircle,
  AlertCircle,
  ExternalLink,
} from "react-feather";
import Swal from "sweetalert2";

import UtiPanCardService from "../../../services/UtiPanCardService";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";

const BuyCoupon = () => {
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState("");
  const [commercialData, setCommercialData] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [errors, setErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!quantity || quantity.trim() === "") {
      newErrors.quantity = "Quantity is required";
    } else if (isNaN(quantity) || parseInt(quantity) <= 0) {
      newErrors.quantity = "Please enter a valid positive number";
    } else if (parseInt(quantity) < 5) {
      newErrors.quantity = "Minimum purchase is 5 coupons";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fetchCommercialData = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setCommercialData(null);

    try {
      const result = await UtiPanCardService.fetchCommercial(
        parseInt(quantity)
      );

      if (!result.success) {
        Swal.fire(
          "Oops!",
          result.message || "Failed to fetch commercial details",
          "error"
        );
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        setCommercialData(responseData.data);
        setShowConfirmModal(true);
      } else {
        Swal.fire(
          "Oops!",
          responseData.message || "Failed to fetch commercial details",
          "error"
        );
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const buyCouponHandler = async () => {
    if (loading) return;

    setLoading(true);

    try {
      const result = await UtiPanCardService.buyCoupon(parseInt(quantity));

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setResponseData(res.data);
        setShowConfirmModal(false);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Coupons purchased successfully!",
          confirmButtonText: "OK",
        });
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />

        <Col md={9}>
          <Card className="shadow-sm p-4 mb-4">
            <div className="d-flex align-items-center mb-4">
              <div className="bg-primary p-2 rounded-circle me-3">
                <ShoppingCart size={24} color="white" />
              </div>
              <div>
                <h4 className="mb-0">Purchase PAN Coupons</h4>
                <p className="text-muted mb-0">
                  Buy UTI PAN application coupons for your customers
                </p>
              </div>
            </div>

            <Alert variant="info" className="d-flex align-items-center mb-4">
              <Info size={20} className="me-2" />
              <div className="small">
                <strong>Note:</strong> Minimum purchase quantity is 5 coupons.
              </div>
            </Alert>

            {/* Purchase Form */}
            <Card className="border mb-4">
              <Card.Body>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold">
                    <Package size={18} className="me-2 text-primary" />
                    Number of Coupons (Quantity)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="5"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="Enter quantity (minimum 5)"
                    className={errors.quantity ? "is-invalid" : ""}
                    disabled={loading}
                  />
                  {errors.quantity && (
                    <Form.Text className="text-danger">
                      {errors.quantity}
                    </Form.Text>
                  )}
                </Form.Group>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={fetchCommercialData}
                    disabled={loading || !quantity}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart size={20} className="me-2" />
                        Submit
                      </>
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {/* Success Response Display */}
            {responseData && (
              <Card className="border-success mb-4">
                <Card.Header className="bg-success text-white">
                  <CheckCircle size={20} className="me-2" />
                  Purchase Successful
                </Card.Header>
                <Card.Body>
                  <Alert variant="success">
                    <Alert.Heading>
                      Coupons Purchased Successfully!
                    </Alert.Heading>
                    <p>
                      Your coupons will be credited to your account within 30
                      minutes to 1 hour.
                    </p>
                    <hr />
                    <div className="mb-0">
                      <strong>Transaction Details:</strong>
                      <ul className="mb-0 mt-2">
                        <li>Quantity: {responseData.quantity || quantity}</li>
                        <li>
                          Transaction ID: {responseData.order_id || "N/A"}
                        </li>
                        <li>Date: {responseData.timestamp || "N/A"}</li>
                      </ul>
                    </div>
                  </Alert>
                </Card.Body>
              </Card>
            )}

            {/* Commercial Details Confirmation Modal */}
            <Modal
              show={showConfirmModal}
              onHide={() => setShowConfirmModal(false)}
              size="lg"
              centered
            >
              <Modal.Header closeButton className="bg-light">
                <Modal.Title>
                  <ShoppingCart size={20} className="me-2 text-primary" />
                  Confirm Purchase
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {commercialData && (
                  <>
                    <Alert
                      variant="warning"
                      className="d-flex align-items-center"
                    >
                      <AlertCircle size={20} className="me-2" />
                      Please review the order details before confirming your
                      purchase.
                    </Alert>

                    <Table bordered responsive className="mb-4">
                      <thead className="table-light">
                        <tr>
                          <th colSpan="2" className="text-center">
                            Order Details
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="table-light">
                          <td width="50%" className="fw-semibold">
                            Quantity
                          </td>
                          <td className="fw-bold">{commercialData.quantity}</td>
                        </tr>

                        <tr className="table-light">
                          <td className="fw-semibold">Order Amount</td>
                          <td className="fw-bold">
                            {formatCurrency(commercialData.order_amount)}
                          </td>
                        </tr>

                        <tr className="table-light">
                          <td className="fw-semibold">Commission</td>
                          <td className="fw-bold text-success">
                            {formatCurrency(commercialData.commercial_amount)}
                          </td>
                        </tr>

                        <tr className="table-light">
                          <td className="fw-semibold">Payable Amount</td>
                          <td className="fw-bold text-danger">
                            {formatCurrency(commercialData.total_price)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    <div className="border rounded p-3 bg-light">
                      <h6 className="mb-2">
                        <Info size={16} className="me-2 text-info" />
                        Important Notes:
                      </h6>
                      <ul className="mb-0 small">
                        <li>
                          Please note that this purchase is non-refundable.
                        </li>
                        <li>
                          Your coupons will be credited to your account within
                          30 minutes to 1 hour after successful payment.
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="success"
                  onClick={buyCouponHandler}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Processing...
                    </>
                  ) : (
                    <>Confirm Purchase</>
                  )}
                </Button>
              </Modal.Footer>
            </Modal>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BuyCoupon;
